import { useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useRecoilValue } from "recoil";
import { userAtom } from "../atoms/atoms";
import { findCustomerFeatures, findCurrentCustomerFeatures } from "../resources/Api/customerParameter/customerFeatures";
import { CustomerFeatureType, UserType } from "../types/types";

export const useCustomerFeatures = (selectedCustomerId?: number): CustomerFeatureType | null => {
    const user = useRecoilValue<UserType>(userAtom);
    const [cookies] = useCookies(["token"]);
    const [fresh, setFresh] = useState<CustomerFeatureType | null>(null);
    const isManager = user.configs.isManager;
    const customerId = isManager ? selectedCustomerId ?? user.customer?.id : user.customer?.id;
    const token: string = cookies.token || sessionStorage.getItem("token") || "";

    useEffect(() => {
        if (!customerId || customerId <= 0 || !token) {
            setFresh(null);
            return;
        }
        let active = true;
        (isManager ? findCustomerFeatures(token, customerId) : findCurrentCustomerFeatures(token))
            .then((result) => { if (active && result.customerId === customerId) setFresh(result); })
            .catch(() => { if (active) setFresh(null); });
        return () => { active = false; };
    }, [customerId, isManager, token]);

    return useMemo(() => {
        if (!customerId || customerId <= 0) return null;
        if (fresh?.customerId === customerId) return fresh;
        const settings = isManager ? null : user.configs.customerSettings;
        return {
            customerId,
            useVideoTelemetry: settings?.useVideoTelemetry ?? false,
            useCheckList: settings?.useCheckList ?? false,
            useTurnstile: settings?.useTurnstile ?? false,
            useWorkSchedules: settings?.useWorkSchedules ?? false,
            useCalculatedRoutes: settings?.useCalculatedRoutes ?? false,
            useWorkJourney: settings?.useWorkSchedules ?? false,
            useDriverControl: settings?.useDriverControl ?? false,
            useDriverLogin: settings?.useDriverLogin ?? false,
            useTransparencyPortalLink: settings?.useTransparencyPortalLink ?? false,
            useIntroTour: settings?.useIntroTour ?? false,
        };
    }, [customerId, fresh, isManager, user.configs.customerSettings]);
};
